import { z } from 'zod';
import { balanceAmountStringSchema } from '../currency';
import { VIP_GOLD_BONUS_PERCENT, VIP_XP_BONUS_PERCENT } from './vip';

/**
 * What `GET /api/economy/me` answers, and nothing more.
 *
 * ONE account's own balances and its own VIP state. There is no field here that
 * names another player, and no route anywhere that lets a client state a
 * balance: money moves through the ledger, under a server-derived idempotency
 * key, or it does not move.
 *
 * Balances travel as decimal STRINGS. A JSON number is a double to every client
 * and to most tooling in between, and the ledger holds forty digits.
 */

/** Reasons a ledger row exists. Distinguishing them is what makes reset computable. */
export const LEDGER_REASONS = [
  'hunt_reward',
  'offline_farm',
  /** Cash bought with real money. The only kind a reset restores. */
  'purchase',
  /** Cash handed out for testing or promotion. Never restored. */
  'promo_grant',
  /** The debit and the credit of one internal transfer. */
  'transfer_out',
  'transfer_in',
  /** Cash put back after an alpha reset. Not a new purchase. */
  'reset_restoration',
  /**
   * A balance zeroed by a release reset. A DEBIT, and an auditable one.
   *
   * The alternative was `UPDATE account_wallets SET balance = 0`, which makes
   * the past disappear: a balance with no movement behind it cannot be
   * explained, reconciled or disputed. This reason exists so "where did my
   * 40 000 Gold go" has an answer with a timestamp, an idempotency key and a
   * run id on it.
   */
  'reset_wipe',
  /** A compensating row. The ledger is append-only; nothing is ever deleted. */
  'purchase_refund',
  /**
   * The direct trade's fixed fee, destroyed. A SINK: no account receives it.
   *
   * Its own reason rather than a debit wearing somebody else's name, so an
   * audit can total exactly what left the economy and tell it apart from a
   * purchase, a refund or a transfer between two players.
   */
  'direct_trade_fee',
  /**
   * The item marketplace's percentage, destroyed. A SINK, like the direct
   * trade's fee - and its own reason, so an audit can total what each system
   * removed from the economy without one hiding inside the other.
   */
  'market_fee',
  /** The buyer's payment, and the seller's net. Two sides, one correlation. */
  'market_purchase',
  'market_sale',
  /**
   * A gacha pull's price, destroyed. A SINK, like the two fees.
   *
   * Its own reason so an audit can total exactly what the gacha removed from
   * the economy - and so spending Cash here is never mistaken for a PURCHASE.
   * The alpha reset restores what was BOUGHT with real money, computed from
   * 'purchase' rows; a spend does not touch that entitlement in either
   * direction.
   */
  'gacha_spend',
  /**
   * What an equipment upgrade attempt cost, destroyed. A SINK, like the fees
   * and the gacha.
   *
   * Its own reason for the same two reasons: an audit can total exactly what
   * upgrading removed from the economy without it hiding inside a fee, and
   * spending Cash here is never mistaken for a PURCHASE - the alpha reset
   * restores what was BOUGHT with real money, and a spend must not touch that
   * entitlement in either direction.
   *
   * One reason for both currencies. The ROW already says which one moved, and
   * a second reason would let the two drift apart in every future query.
   */
  'equipment_upgrade',
  /**
   * What one roll of the Cash skin roulette cost, destroyed. A SINK, like the
   * fees, the pet gacha and the upgrade (ADR 0040).
   *
   * Its OWN reason rather than reusing `gacha_spend`, and the distinction is
   * not cosmetic: the two banners sell different things at different prices out
   * of different pools, and an audit that could not separate "what the pet
   * gacha removed" from "what the skin roulette removed" would be unable to
   * answer either question. Cash only - there is no Gold price for a skin.
   *
   * A spend is never a PURCHASE. The alpha reset restores what was BOUGHT with
   * real money, computed from 'purchase' rows, and this must not touch that
   * entitlement in either direction.
   */
  'skin_gacha_spend',
  /**
   * O ouro de campo de uma expedição, ao virar Ouro seguro (gate V28I.G).
   *
   * Um CRÉDITO, e a única entrada por onde o ouro do mundo aberto entra na
   * economia. Razão própria, e não `hunt_reward`, porque as duas respondem a
   * perguntas diferentes: uma auditoria tem de poder somar exatamente o que a
   * caça pagou e exatamente o que uma expedição trouxe, e uma escondida dentro
   * da outra deixaria as duas sem resposta.
   *
   * O ouro de campo vive FORA do livro-razão até este instante. Ele cai quando
   * o piloto morre, muda de dono quando alguém saqueia a caixa, e só existe
   * como Ouro quando uma extração o compromete - que é o que faz "morrer custa"
   * ser verdade em vez de uma anotação.
   */
  'open_world_extraction',
  /**
   * **A venda de uma captura ao comerciante de um porto seguro.**
   *
   * Um CRÉDITO, e uma razão própria pela mesma razão que a extração tem a
   * dela: uma auditoria tem de poder somar exatamente o que os peixes
   * pagaram, e escondê-los dentro de `open_world_extraction` deixaria as
   * duas perguntas sem resposta.
   *
   * **Só a coleção segura passa por aqui.** Vender um peixe que ainda está
   * na carga de campo credita ouro DE CAMPO — que cai na morte e só se torna
   * Ouro na extração — e o livro-razão não o vê nesse instante. Se as duas
   * vendas escrevessem aqui, o livro diria que o jogador tinha ouro seguro
   * que ele ainda podia perder ao morrer.
   */
  'open_world_fish_sale',
  /**
   * **O Ouro de um planeta minerado.**
   *
   * Um CRÉDITO, e Ouro seguro: a mineração credita pelo livro-razão com uma
   * chave que o servidor deriva do planeta, e espelha a carteira só quando
   * uma linha foi mesmo acrescentada.
   *
   * **Ela já era escrita, e não estava aqui.** O `insert` cru tipa `reason`
   * como `string`, portanto o literal escapava à união e nenhuma auditoria
   * que percorra esta lista via o ouro de mineração. O que muda é o censo:
   * a liquidação, os valores, o destinatário e a idempotência são
   * exactamente os mesmos.
   */
  'mined_planet',
] as const;
export type LedgerReason = (typeof LEDGER_REASONS)[number];
export const ledgerReasonSchema = z.enum(LEDGER_REASONS);

/**
 * Which reasons count as a real purchase for the alpha reset.
 *
 * Stated as data rather than as an `if` scattered through the service: the
 * entitlement calculation, the tests and any future audit all read this list,
 * so "a promo grant is not a purchase" is one fact instead of three.
 */
export const PURCHASE_REASONS: readonly LedgerReason[] = Object.freeze(['purchase']);

export const vipStateSchema = z.object({
  active: z.boolean(),
  /** ISO instants, from the SERVER's clock. Null when there has never been one. */
  startsAt: z.string().nullable(),
  endsAt: z.string().nullable(),
  /** Never negative: an expired window reports zero, not a countdown past zero. */
  remainingSeconds: z.number().int().min(0),
  xpBonusPercent: z.literal(VIP_XP_BONUS_PERCENT),
  goldBonusPercent: z.literal(VIP_GOLD_BONUS_PERCENT),
  /**
   * May this account take part in a DIRECT trade? VIP only, both sides.
   *
   * Named for the thing it gates rather than "market": the item marketplace is
   * open to everybody and a single `marketAllowed` flag would have quietly made
   * it VIP-only the first time somebody reused it.
   */
  directTradeAllowed: z.boolean(),
});
export type VipStateWire = z.infer<typeof vipStateSchema>;

export const economyStateSchema = z.object({
  gold: balanceAmountStringSchema,
  cash: balanceAmountStringSchema,
  vip: vipStateSchema,
});
export type EconomyStateWire = z.infer<typeof economyStateSchema>;

/**
 * The economy's ENTIRE public surface: one authenticated GET.
 *
 * There is no companion POST and there must never be one. Crediting, debiting,
 * transferring, granting VIP and settling a purchase are internal service
 * operations with no route, no flag and no query string - a browser can ask
 * what it has, and nothing else.
 */
export const ECONOMY_ME_ROUTE = '/api/economy/me' as const;

export const economyMeResponseSchema = economyStateSchema;
export type EconomyMeResponse = z.infer<typeof economyMeResponseSchema>;
